import { useCallback, useState } from 'react';

/**
 * useAnnouncer — a polite aria-live message source that speaks EVERY time,
 * including when the same outcome repeats.
 *
 * Authority: WI-POST-BUTTON-REGRESSION-HUNT-001 (the CEO's third report on the
 * Post Studio Post button), building on the #950/#954 announcer pattern.
 *
 * THE DEFECT THIS EXISTS TO PREVENT
 * The obvious spelling of an announcer is:
 *
 *     const [statusMessage, setStatusMessage] = useState('');
 *     const announce = useCallback((msg) => setStatusMessage(msg), []);
 *
 * That is silently broken for a REPEATED outcome. React bails out of a state
 * update whose next value is Object.is-equal to the current one — no re-render,
 * no DOM write. A screen reader speaks a polite live region only when its
 * content CHANGES. So the second and every later identical message is NEVER
 * spoken, while sighted users keep seeing the toast and the code looks correct.
 *
 * How it reached the CEO: LinkedIn publishing is hard-paused (CEO Decision 9),
 * so every press of Post returns the byte-identical 409 reason. The first press
 * announced; presses 2..n were silent. Production dev_events recorded 8 refusals
 * against the same saved post, twice inside 2.5 minutes. Repeating an action
 * that appears to do nothing is the natural thing to do — which means the repeat
 * case is the one that matters most, not an edge case.
 *
 * Why a trailing marker rather than clear-then-set: clearing and re-setting in
 * the same tick is batched into one commit by React, so an identical message
 * collapses back to a no-op — the bug survives. Splitting it across ticks with a
 * timer re-introduces the race and makes every caller async. Alternating an
 * invisible trailing character keeps the update synchronous, deterministic and
 * race-free; assistive tech treats trailing whitespace as nothing, so the CEO
 * hears the message and never the mechanism.
 *
 * Usage — mirrors useState's shape:
 *     const [statusMessage, announce] = useAnnouncer();
 *     <div role="status" aria-live="polite" className="sr-only">{statusMessage}</div>
 *
 * Known remaining adopters (same defect, each its own lane — see the WI handoff):
 * CockpitLayout.jsx, LinkedInPostDetail.jsx, DealSourcingAgent.jsx,
 * DealCard.jsx, StartupReviewDetail.jsx.
 */

// A non-breaking space: invisible on screen, whitespace to assistive tech, and
// enough of a change to force React to commit and the live region to re-speak.
const REPEAT_MARKER = ' ';

export default function useAnnouncer() {
    const [statusMessage, setStatusMessage] = useState('');

    const announce = useCallback((msg) => {
        const next = msg == null ? '' : String(msg);
        // Nothing to say: clear the region outright. Marking an empty message
        // would leave a stray character sitting in a region meant to be silent.
        if (next === '') {
            setStatusMessage('');
            return;
        }
        // Functional update: decide against the CURRENT value, so back-to-back
        // calls in one tick can't read a stale `statusMessage` and collapse.
        setStatusMessage((prev) => (prev === next ? `${next}${REPEAT_MARKER}` : next));
    }, []);

    return [statusMessage, announce];
}
